import React, { useEffect, useState } from 'react';
import { Save, Loader2, Mail, Send, CheckCircle2, XCircle, Info, ShieldCheck, ShieldOff, Copy, MessageCircle } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Btn, Input, Textarea } from './shared';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

// "__VOIPINGER_PASSWORD_UNCHANGED__" — backend constant. Stored here as a
// fragment so the UI can detect when the password field is still showing
// the saved-but-masked placeholder and avoid overwriting it.
const PASSWORD_PLACEHOLDER = '__VOIPINGER_PASSWORD_UNCHANGED__';

export default function AdminSettings() {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SMTP state — loaded separately from /api/admin/smtp-settings.
  const [smtp, setSmtp] = useState(null);
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpEffective, setSmtpEffective] = useState(null);
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);

  // Live chat widget state.
  const [chat, setChat] = useState({ enabled: false, provider: 'tawk', widget_id: '', custom_html: '' });
  const [chatSaving, setChatSaving] = useState(false);

  // 2FA state.
  const twofaEnabled = !!user?.twofa_enabled;
  const [twofaSetup, setTwofaSetup] = useState(null); // { qr_data_url, secret }
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaBusy, setTwofaBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    api.get('/api/admin/settings')
      .then((r) => setForm(r.data || {}))
      .catch((err) => toast({ title: 'Load failed', description: formatApiError(err) }))
      .finally(() => setLoading(false));

    api.get('/api/admin/smtp-settings')
      .then((r) => {
        const saved = r.data?.saved || {};
        setSmtp({
          host: saved.host || '',
          port: saved.port || 587,
          user: saved.user || '',
          password: saved.password === PASSWORD_PLACEHOLDER ? PASSWORD_PLACEHOLDER : '',
          from_email: saved.from_email || '',
          tls: saved.tls !== undefined ? !!saved.tls : true,
          ssl: saved.ssl !== undefined ? !!saved.ssl : false,
          enabled: saved.enabled !== undefined ? !!saved.enabled : true,
          recipients: saved.recipients || '',
        });
        setSmtpEffective(r.data?.effective || null);
        setSmtpPasswordSet(!!r.data?.password_set);
      })
      .catch(() => { /* non-fatal: section just shows defaults */ })
      .finally(() => setSmtpLoading(false));

    api.get('/api/admin/chat-widget')
      .then((r) => {
        const c = r.data || {};
        setChat({
          enabled: !!c.enabled,
          provider: c.provider || 'tawk',
          widget_id: c.widget_id || '',
          custom_html: c.custom_html || '',
        });
      })
      .catch(() => { /* defaults */ });
  }, [toast]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateSmtp = (k, v) => setSmtp((s) => ({ ...s, [k]: v }));
  const updateChat = (k, v) => setChat((c) => ({ ...c, [k]: v }));

  const saveChat = async () => {
    setChatSaving(true);
    try {
      await api.put('/api/admin/chat-widget', chat);
      toast({ title: 'Chat widget saved', description: chat.enabled ? 'Live on the public site after a refresh.' : 'Disabled.' });
    } catch (err) {
      toast({ title: 'Save failed', description: formatApiError(err) });
    } finally {
      setChatSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/settings', form);
      toast({ title: 'Settings saved' });
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      // If password field still holds the placeholder, drop it so the
      // backend doesn't clobber the saved value.
      const body = { ...smtp };
      if (body.password === PASSWORD_PLACEHOLDER || body.password === '') delete body.password;
      await api.put('/api/admin/smtp-settings', body);
      toast({ title: 'SMTP settings saved' });
      // Re-load to refresh the "effective" preview + password_set badge.
      const r = await api.get('/api/admin/smtp-settings');
      const saved = r.data?.saved || {};
      setSmtp((prev) => ({
        ...prev,
        password: saved.password === PASSWORD_PLACEHOLDER ? PASSWORD_PLACEHOLDER : '',
      }));
      setSmtpEffective(r.data?.effective || null);
      setSmtpPasswordSet(!!r.data?.password_set);
    } catch (err) {
      toast({ title: 'SMTP save failed', description: formatApiError(err) });
    } finally {
      setSmtpSaving(false);
    }
  };

  const sendTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      const r = await api.post('/api/admin/smtp-test', {});
      const data = r.data || {};
      if (data.success) {
        toast({
          title: 'Test email sent',
          description: `Delivered via ${data.host} to ${(data.recipients || []).join(', ')}`,
        });
      } else {
        toast({ title: 'Test email failed', description: data.error || 'Unknown error' });
      }
    } catch (err) {
      toast({ title: 'Test email failed', description: formatApiError(err) });
    } finally {
      setSmtpTesting(false);
    }
  };

  // ----- 2FA -----
  const startTwofaSetup = async () => {
    setTwofaBusy(true);
    setBackupCodes(null);
    try {
      const res = await api.post('/api/auth/2fa/setup', {});
      setTwofaSetup(res.data);
    } catch (err) {
      toast({ title: '2FA setup failed', description: formatApiError(err) });
    } finally {
      setTwofaBusy(false);
    }
  };

  const confirmTwofa = async () => {
    setTwofaBusy(true);
    try {
      const res = await api.post('/api/auth/2fa/verify-setup', { code: twofaCode });
      setBackupCodes(res.data?.backup_codes || []);
      setTwofaSetup(null);
      setTwofaCode('');
      toast({ title: '2FA enabled' });
      refresh?.();
    } catch (err) {
      toast({ title: 'Verification failed', description: formatApiError(err) });
    } finally {
      setTwofaBusy(false);
    }
  };

  const disableTwofa = async () => {
    if (!disableCode) { toast({ title: 'Enter a current 2FA code to disable' }); return; }
    setTwofaBusy(true);
    try {
      await api.post('/api/auth/2fa/disable', { code: disableCode });
      setDisableCode('');
      toast({ title: '2FA disabled' });
      refresh?.();
    } catch (err) {
      toast({ title: 'Disable failed', description: formatApiError(err) });
    } finally {
      setTwofaBusy(false);
    }
  };

  if (loading || !form) return <Loading />;

  return (
    <>
      <PageHeader
        title="Site Settings"
        subtitle="Global site information — phone, email, hero text"
        action={<Btn onClick={save} disabled={saving} testId="settings-save">{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}</Btn>}
      />

      <AdminCard title="Contact info">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Brand" value={form.brand || ''} onChange={(e) => update('brand', e.target.value)} />
          <Input label="Phone (display)" value={form.phone || ''} onChange={(e) => update('phone', e.target.value)} />
          <Input label="Phone (tel:)" value={form.phoneTel || ''} onChange={(e) => update('phoneTel', e.target.value)} placeholder="+442033551151" />
          <Input label="Email" value={form.email || ''} onChange={(e) => update('email', e.target.value)} />
          <Input label="Address" value={form.address || ''} onChange={(e) => update('address', e.target.value)} className="md:col-span-2" />
          <Input label="Hours" value={form.hours || ''} onChange={(e) => update('hours', e.target.value)} />
          <Input label="Login URL" value={form.loginUrl || ''} onChange={(e) => update('loginUrl', e.target.value)} />
          <Input label="YouTube URL" value={form.youtube || ''} onChange={(e) => update('youtube', e.target.value)} />
          <Input label="LinkedIn URL" value={form.linkedin || ''} onChange={(e) => update('linkedin', e.target.value)} />
        </div>
      </AdminCard>

      <div className="h-4" />

      <AdminCard title="Homepage hero" subtitle="Main headline on the landing page">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Hero badge" value={form.heroBadge || ''} onChange={(e) => update('heroBadge', e.target.value)} className="md:col-span-2" />
          <Input label="Hero title" value={form.heroTitle || ''} onChange={(e) => update('heroTitle', e.target.value)} />
          <Input label="Hero title highlight" value={form.heroTitleHighlight || ''} onChange={(e) => update('heroTitleHighlight', e.target.value)} />
          <Textarea label="Hero subtitle" rows={3} value={form.heroSubtitle || ''} onChange={(e) => update('heroSubtitle', e.target.value)} className="md:col-span-2" />
          <Input label="Hero primary CTA" value={form.heroPrimaryCta || ''} onChange={(e) => update('heroPrimaryCta', e.target.value)} />
        </div>
      </AdminCard>

      <div className="mt-6 flex justify-end">
        <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}</Btn>
      </div>

      <div className="h-8" />

      {/* ===== SMTP / lead notification email ===== */}
      <AdminCard
        title={<span className="flex items-center gap-2"><Mail size={16} /> Lead notification emails (SMTP)</span>}
        subtitle="Where contact, partner and number-order submissions are emailed to. Office 365 settings below — use an app password if MFA is on."
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={sendTestSmtp} disabled={smtpTesting || smtpLoading} testId="smtp-test">
              {smtpTesting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send test email</>}
            </Btn>
            <Btn onClick={saveSmtp} disabled={smtpSaving || smtpLoading} testId="smtp-save">
              {smtpSaving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save SMTP</>}
            </Btn>
          </div>
        }
      >
        {smtpLoading || !smtp ? (
          <Loading />
        ) : (
          <>
            {/* Effective config summary */}
            {smtpEffective && (
              <div className="mb-5 rounded-xl bg-violet-50/60 border border-violet-200 px-4 py-3 text-xs text-slate-700 flex items-start gap-2">
                <Info size={14} className="text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-violet-900">Effective configuration:</span>{' '}
                  Host <code className="font-mono">{smtpEffective.host || '—'}:{smtpEffective.port || '—'}</code> ·{' '}
                  From <code className="font-mono">{smtpEffective.from_email || '—'}</code> ·{' '}
                  Notifications {' '}
                  {smtpEffective.host && smtpEffective.enabled ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={11} /> live</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-700"><XCircle size={11} /> off</span>
                  )}
                  {' · '}Recipients <code className="font-mono">{smtpEffective.recipients || '—'}</code>
                  <div className="text-[11px] text-slate-500 mt-1">
                    DB settings override <code>SMTP_*</code> env vars override the in-code defaults
                    (<code>emrah@clisecure.com, info@voipinger.com</code>).
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="SMTP host"
                value={smtp.host}
                onChange={(e) => updateSmtp('host', e.target.value)}
                placeholder="smtp.office365.com"
              />
              <Input
                label="Port"
                type="number"
                value={smtp.port}
                onChange={(e) => updateSmtp('port', Number(e.target.value) || 587)}
                placeholder="587"
              />
              <Input
                label="SMTP user (mailbox)"
                value={smtp.user}
                onChange={(e) => updateSmtp('user', e.target.value)}
                placeholder="emrah@clisecure.com"
              />
              <Input
                label={smtpPasswordSet ? 'Password (leave to keep saved)' : 'Password / app password'}
                type="password"
                value={smtp.password === PASSWORD_PLACEHOLDER ? '' : smtp.password}
                onChange={(e) => updateSmtp('password', e.target.value)}
                placeholder={smtpPasswordSet ? '•••••••• (already saved)' : 'App password (Office 365)'}
                autoComplete="new-password"
              />
              <Input
                label="From email"
                value={smtp.from_email}
                onChange={(e) => updateSmtp('from_email', e.target.value)}
                placeholder="emrah@clisecure.com — must match the user for Office 365"
              />
              <div className="flex items-center gap-6 mt-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!smtp.tls}
                    onChange={(e) => updateSmtp('tls', e.target.checked)}
                    className="w-4 h-4 accent-fuchsia-500"
                  />
                  <span className="text-sm text-slate-300">STARTTLS (port 587)</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!smtp.ssl}
                    onChange={(e) => updateSmtp('ssl', e.target.checked)}
                    className="w-4 h-4 accent-fuchsia-500"
                  />
                  <span className="text-sm text-slate-300">SSL wrap (port 465)</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!smtp.enabled}
                    onChange={(e) => updateSmtp('enabled', e.target.checked)}
                    className="w-4 h-4 accent-fuchsia-500"
                  />
                  <span className="text-sm text-slate-300">Notifications enabled</span>
                </label>
              </div>
              <Textarea
                label="Recipients (comma-separated)"
                rows={2}
                value={smtp.recipients}
                onChange={(e) => updateSmtp('recipients', e.target.value)}
                placeholder="emrah@clisecure.com, info@voipinger.com"
                className="md:col-span-2"
              />
            </div>

            <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
              <p>
                <strong>Office 365 quick tip:</strong> "SMTP user" + "From email" must match,
                and the mailbox needs <em>Authenticated SMTP</em> enabled at{' '}
                <a href="https://admin.microsoft.com" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">
                  admin.microsoft.com
                </a>{' '}
                → Users → Active users → [your mailbox] → Mail → Manage email apps. Use an app password
                (mysignins.microsoft.com → Security info → Add app password) when MFA is on.
              </p>
            </div>
          </>
        )}
      </AdminCard>

      <div className="h-8" />

      {/* ===== Two-factor authentication ===== */}
      <AdminCard
        title={<span className="flex items-center gap-2"><ShieldCheck size={16} /> Two-factor authentication</span>}
        subtitle="Protect your admin account with a time-based one-time code (Google Authenticator, Authy, 1Password)."
      >
        {twofaEnabled ? (
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-bold">
              <ShieldCheck size={14} /> 2FA is ON for {user?.email}
            </div>
            <p className="text-sm text-slate-500">
              To turn it off, enter a current code from your authenticator app (or a backup code).
            </p>
            <div className="flex flex-wrap gap-2 items-end max-w-md">
              <Input
                label="Current 2FA code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="000000"
                className="font-mono"
              />
              <Btn variant="danger" onClick={disableTwofa} disabled={twofaBusy}>
                {twofaBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldOff size={14} />} Disable 2FA
              </Btn>
            </div>
          </div>
        ) : twofaSetup ? (
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">1. Scan this QR code</p>
              <img src={twofaSetup.qr_data_url} alt="2FA QR code" className="w-44 h-44 rounded-xl border border-slate-200 bg-white p-2" />
              <p className="text-[11px] text-slate-500 mt-2">
                Can&rsquo;t scan? Enter this key manually:<br />
                <code className="font-mono text-slate-700 break-all">{twofaSetup.secret}</code>
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">2. Enter the 6-digit code</p>
              <Input
                value={twofaCode}
                onChange={(e) => setTwofaCode(e.target.value)}
                placeholder="000000"
                className="font-mono text-lg tracking-widest"
              />
              <div className="flex gap-2 mt-3">
                <Btn onClick={confirmTwofa} disabled={twofaBusy || twofaCode.length < 6}>
                  {twofaBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Enable 2FA
                </Btn>
                <Btn variant="ghost" onClick={() => { setTwofaSetup(null); setTwofaCode(''); }}>Cancel</Btn>
              </div>
            </div>
          </div>
        ) : backupCodes ? (
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-bold">
              <ShieldCheck size={14} /> 2FA enabled
            </div>
            <p className="text-sm text-slate-700 font-semibold">
              Save these backup codes somewhere safe — each works once if you lose your authenticator.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg">
              {backupCodes.map((c) => (
                <code key={c} className="text-center font-mono text-sm bg-slate-100 border border-slate-200 rounded-lg py-2 text-slate-800">{c}</code>
              ))}
            </div>
            <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast({ title: 'Backup codes copied' }); }}>
              <Copy size={14} /> Copy all
            </Btn>
            <p className="text-[11px] text-slate-500">These won&rsquo;t be shown again. If you lose them, disable + re-enable 2FA to get a new set.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 border border-slate-200 text-sm font-bold">
              <ShieldOff size={14} /> 2FA is OFF
            </div>
            <p className="text-sm text-slate-500 max-w-xl">
              Strongly recommended for an admin account. You&rsquo;ll scan a QR code with your
              authenticator app, then enter a 6-digit code at every login.
            </p>
            <Btn onClick={startTwofaSetup} disabled={twofaBusy}>
              {twofaBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Set up 2FA
            </Btn>
          </div>
        )}
      </AdminCard>

      <div className="h-8" />

      {/* ===== Live chat widget ===== */}
      <AdminCard
        title={<span className="flex items-center gap-2"><MessageCircle size={16} /> Live chat widget</span>}
        subtitle="Drop a live-chat bubble onto the public site. Toggle it on/off without touching code."
        action={
          <Btn onClick={saveChat} disabled={chatSaving}>
            {chatSaving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save chat</>}
          </Btn>
        }
      >
        <div className="space-y-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!chat.enabled} onChange={(e) => updateChat('enabled', e.target.checked)} className="w-4 h-4 accent-fuchsia-500" />
            <span className="text-sm font-semibold text-slate-200">Show chat widget on the public site</span>
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Provider</label>
              <select
                value={chat.provider}
                onChange={(e) => updateChat('provider', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm"
              >
                <option value="tawk">Tawk.to (free)</option>
                <option value="crisp">Crisp</option>
                <option value="custom">Custom embed</option>
              </select>
            </div>
            {chat.provider !== 'custom' && (
              <Input
                label={chat.provider === 'tawk' ? 'Tawk property/widget (propertyId/widgetId)' : 'Crisp website ID'}
                value={chat.widget_id}
                onChange={(e) => updateChat('widget_id', e.target.value)}
                placeholder={chat.provider === 'tawk' ? '5f1a.../default' : 'xxxxxxxx-xxxx-...'}
              />
            )}
          </div>

          {chat.provider === 'custom' && (
            <Textarea
              label="Custom embed HTML (script snippet)"
              rows={5}
              value={chat.custom_html}
              onChange={(e) => updateChat('custom_html', e.target.value)}
              placeholder="<script>…your provider's widget snippet…</script>"
            />
          )}

          <div className="text-[11px] text-slate-500 leading-relaxed">
            {chat.provider === 'tawk' && (
              <p><strong>Tawk.to:</strong> free forever. Sign up at <a href="https://tawk.to" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">tawk.to</a>, then Admin → Channels → Chat Widget → copy the property + widget ID from the embed URL (<code>embed.tawk.to/PROPERTY/WIDGET</code>). Enter as <code>PROPERTY/WIDGET</code>.</p>
            )}
            {chat.provider === 'crisp' && (
              <p><strong>Crisp:</strong> sign up at <a href="https://crisp.chat" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">crisp.chat</a>, then Settings → Website Settings → Setup → copy your <code>Website ID</code>.</p>
            )}
            {chat.provider === 'custom' && (
              <p>Paste any provider's full embed snippet (Intercom, LiveChat, Drift, etc.). Scripts run on every public page.</p>
            )}
          </div>
        </div>
      </AdminCard>
    </>
  );
}
